<template>
  <div class="animated fadeIn">
    <b-card-header>
      Customer Information
      <div class="card-header-actions">
        <router-link class="card-header-action" :to="{ name: 'Verifikasi KYC' }">
          <small class="text-muted">Back</small>
        </router-link>
      </div>
    </b-card-header>
    <b-card>
      <div v-if="isLoading" class="d-flex justify-content-center py-4">
        <b-spinner small label="Loading..."></b-spinner>
      </div>
      <b-row v-else class="mb-2">
        <b-col sm="4" v-for="item in customerItems" :key="item.label" class="mb-4">
          <h6>{{ item.label }}</h6>
          <p :class="item.className || ''"><strong>{{ item.value }}</strong></p>
          <div v-if="item.label === 'Final AML Score'" class="aml-score-slider">
            <div class="aml-score-track"></div>
            <div class="aml-score-thumb" :style="{ left: scorePosition + '%' }">
              <span>{{ normalizedScore }}</span>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <b-card-header>
      <div class="d-flex align-items-center justify-content-between">
        <span>Response Screening AML</span>
        <small class="text-muted">{{ responseDatetime }}</small>
      </div>
    </b-card-header>
    <b-card>
      <div v-if="isLoading" class="d-flex justify-content-center py-4">
        <b-spinner small label="Loading..."></b-spinner>
      </div>
      <div 
        class="wrapper-log-detail" 
        v-else-if="normalizedLogs.length === 0"
      >
        <p>Data Belum Tersedia!</p>
      </div>
      <div v-else>
        <b-card no-body v-for="(item, index) in normalizedLogs" :key="item.key" class="mb-2">
          <b-card-header header-tag="header" class="p-1 aml-log-header" role="tab">
            <b-button 
              block 
              v-b-toggle="'aml-response-' + item.key" 
              class="text-left font-weight-bold aml-collapse-toggle" 
              variant="light"
            >
              Data {{ index + 1 }} | {{ amlLogSource(item) }}
            </b-button>
          </b-card-header>
          <b-collapse :id="'aml-response-' + item.key" class="p-3">
            <b-row>
              <b-col sm="4" class="mb-3" v-for="col in logColumns(item)" :key="col.label">
                <h6>{{ col.label }}</h6>
                <p v-if="col.type === 'link' && col.value !== '-'">
                  <a :href="col.value" target="_blank" rel="noopener noreferrer">View PDF</a>
                </p>
                <p v-else><strong>{{ col.value }}</strong></p>
              </b-col>
            </b-row>
          </b-collapse>
        </b-card>
      </div>
    </b-card>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "DetailProfileRiskAml",
  data() {
    return {
      isLoading: false,
      amlDetail: {},
      amlLogs: [],
      errors: {
        code: "",
        message: "",
        status: "",
      },
    };
  },
  computed: {
    occupationNumericScore() {
      return this.toNumberOrNull(this.pick(this.amlDetail, ["occupation_score"]));
    },
    regionNumericScore() {
      return this.toNumberOrNull(this.pick(this.amlDetail, ["region_score"]));
    },
    aumNumericScore() {
      return this.toNumberOrNull(this.pick(this.amlDetail, ["aum_score"]));
    },
    combinedSraNumericScore() {
      if (this.occupationNumericScore !== null && this.regionNumericScore !== null) {
        return (this.occupationNumericScore + this.regionNumericScore) / 2;
      }
      const fallbackCombined = this.toNumberOrNull(this.pick(this.amlDetail, ["combined_score"]));
      return fallbackCombined;
    },
    sraRiskNumericScore() {
      return this.toNumberOrNull(this.pick(this.amlDetail, ["sra_score", "sraRiskScore"]));
    },
    rawWeightedScore() {
      return this.toNumberOrNull(this.pick(this.amlDetail, ["final_score", "aml_score", "score"]));
    },
    normalizedScore() {
      const score = this.rawWeightedScore;
      if (score === null) return 1;
      if (score >= 3) return 3;
      if (score <= 1) return 1;
      return score || 1;
    },
    scorePosition() {
      const map = { 1: 0, 2: 50, 3: 100 };
      return map[this.normalizedScore] !== undefined ? map[this.normalizedScore] : 0;
    },
    responseDatetime() {
      const dt = this.pick(this.amlDetail, ["rs_datetime", "screening_date", "created_at"], "-");
      if (dt === "-") return "-";
      const parsed = moment(dt);
      return parsed.isValid() ? parsed.format("DD MMM YYYY HH:mm:ss") : dt;
    },
    customerItems() {
      return [
        { label: "Email", value: this.pick(this.amlDetail, ["email"]) },
        { label: "Full Name", value: this.pick(this.amlDetail, ["full_name", "name"]) },
        { label: "NIK", value: this.pick(this.amlDetail, ["nik", "id_number", "ktp"]) },
        { label: "Occupation Score", value: this.riskTextByNineScale(this.occupationNumericScore) },
        { label: "Region Score", value: this.riskTextByNineScale(this.regionNumericScore) },
        { label: "AUM Score", value: this.riskTextByAumScale(this.aumNumericScore) },
        { label: "Combined SRA", value: this.riskTextByCombinedSra(this.combinedSraNumericScore) },
        { label: "SRARiskScore", value: this.riskTextBySraScale(this.sraRiskNumericScore) },
        {
          label: "Final AML Score",
          value: this.riskTextByFinalWeighted(this.rawWeightedScore),
          className: this.finalAmlClass(this.normalizedScore),
        },
      ];
    },
    normalizedLogs() {
      const logs = [];

      this.amlLogs.forEach((rawItem, rawIndex) => {
        const responseRoot = rawItem && rawItem.attachment && typeof rawItem.attachment === "object" ? rawItem.attachment : rawItem;
        const responseData = responseRoot && Array.isArray(responseRoot.data) ? responseRoot.data : null;

        if (responseData && responseData.length > 0) {
          responseData.forEach((entry, entryIndex) => {
            logs.push({
              key: `${rawItem.id || rawIndex}-${entryIndex}`,
              raw: rawItem,
              root: responseRoot,
              entry: entry,
            });
          });
        } else {
          logs.push({
            key: `${rawItem.id || rawIndex}`,
            raw: rawItem,
            root: responseRoot || {},
            entry: responseRoot || {},
          });
        }
      });

      return logs;
    },
  },
  created() {
    this.fetchDetail();
  },
  methods: {
    toNumberOrNull(value) {
      if (value === undefined || value === null || value === "") return null;
      const number = Number(value);
      return Number.isNaN(number) ? null : number;
    },
    riskLabelByNineScale(score) {
      if (score === null) return "-";
      if (score <= 3) return "Low Risk";
      if (score <= 6) return "Medium Risk";
      return "High Risk";
    },
    riskTextByNineScale(score) {
      if (score === null) return "-";
      return `${this.riskLabelByNineScale(score)} (${score})`;
    },
    riskTextByAumScale(score) {
      if (score === null) return "-";
      if (score <= 1) return `Low Risk (${score})`;
      if (score <= 2) return `Medium Risk (${score})`;
      return `High Risk (${score})`;
    },
    riskTextByCombinedSra(score) {
      if (score === null) return "-";
      const rounded = Number.isInteger(score) ? score : Number(score.toFixed(1));
      if (score <= 3) return `Low Risk (${rounded})`;
      if (score <= 6) return `Medium Risk (${rounded})`;
      return `High Risk (${rounded})`;
    },
    riskTextBySraScale(score) {
      if (score === null) return "-";
      if (score <= 1) return `Low Risk (${score})`;
      if (score <= 2) return `Medium Risk (${score})`;
      return `High Risk (${score})`;
    },
    riskTextByFinalWeighted(score) {
      if (score === null) return "-";
      if (score <= 1) return `Low Risk (${score})`;
      if (score <= 2) return `Medium Risk (${score})`;
      return `High Risk (${score})`;
    },
    pick(obj, keys, fallback = "-") {
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (obj && obj[key] !== undefined && obj[key] !== null && obj[key] !== "") return obj[key];
      }
      return fallback;
    },
    finalAmlClass(score) {
      if (Number(score) === 1) return "text-black";
      if (Number(score) === 2) return "text-orange";
      if (Number(score) === 3) return "text-red";
      return "";
    },
    amlLogSource(item) {
      const source = this.pick(item.entry, ["source_info"], "-");
      return String(source).toUpperCase();
    },
    logColumns(item) {
      const entry = item && item.entry ? item.entry : {};
      const root = item && item.root ? item.root : {};

      const finalPdf = this.pick(
        entry,
        ["url_pdf_detail"],
        this.pick(root, ["url_pdf_detail"], "-")
      );

      const similarity = this.pick(
        entry,
        ["similarity"],
        this.pick(root, ["similarity"], "-")
      );

      return [
        {
          label: "Full Name",
          value: this.pick(entry, ["full_name"], '-'),
        },
        { label: "Similiarity Percentage", value: similarity === "-" ? "-" : `${similarity}%` },
        {
          label: "Occupation Category",
          value: this.pick(
            entry,
            ["occupation_category"],
            '-'
          ),
        },
        {
          label: "Date of Birth",
          value: this.pick(entry, ["birth_date"], this.pick(root, ["birth_date"], "-")),
        },
        {
          label: "Citizen",
          value: this.pick(entry, ["country_name"], this.pick(root, ["country_name"], "-")),
        },
        { label: "Attachment PDF", value: finalPdf, type: "link" },
      ];
    },
    fetchDetail() {
      this.isLoading = true;
      const decodedId = atob(this.$route.params.id);
      const detailReq = this.$http.get(`aml/${decodedId}`);
      const logsReq = this.$http.get(`aml/${decodedId}/logs`);

      Promise.all([detailReq, logsReq])
        .then(([detailResult, logsResult]) => {
          this.amlDetail = detailResult.data.data || detailResult.data || {};
          this.amlLogs = Array.isArray(logsResult.data) ? logsResult.data : (logsResult.data && logsResult.data.data) || [];
        })
        .catch((error) => {
          this.handleLoadError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    handleLoadError(error) {
      this.errors.code = error.response.data.meta.code;
      this.errors.message = error.response.data.meta.message;
      this.errors.status = error.response.data.meta.code;
      if (this.errors.code == 401) this.$router.push("/login");
      else if (this.errors.code == 403) this.$router.push("/403");
      else if (this.errors.code == 500) this.$router.push("/500");
      else this.$swal.fire("Error!", this.errors.message || "Something went wrong. Please try again later.", "error");
    },
  },
};
</script>

<style>
.text-red { color: #cb211f; }
.text-black { color: #2f353a; }
.text-orange { color: #f08f00; }

.wrapper-log-detail {
  width: 100%;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.aml-score-slider {
  position: relative;
  height: 28px;
  margin-top: -8px;
  max-width: 180px;
  width: 100%;
}

.aml-score-track {
  position: absolute;
  left: 0;
  right: 0;
  top: 11px;
  height: 8px;
  border-radius: 10px;
  background: linear-gradient(90deg, #35aa4b 0%, #d1b331 50%, #cb211f 100%);
}

.aml-score-thumb {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: #ffffff;
  border: 2px solid #73818f;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #2f353a;
}

.aml-log-header { background-color: #f0f3f5; }

.aml-collapse-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.aml-collapse-toggle::after {
  content: "\f106";
  font-family: FontAwesome;
  color: #606060;
  font-size: 14px;
}

.aml-collapse-toggle.collapsed::after { content: "\f107"; }
</style>